<template>
  <div class="CartPaypal flex-1 flex justify-center px-4">
    <div :id="buttons[0]" class="w-full" style="height: 50px"></div>
    <Loading v-if="processing"> Please wait while we process your order </Loading>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from "vue"
import fetchWindowObject from "@/lib/fetchWindowObject"
import Http from "@/lib/Http"
import Loading from "./Loading.vue"
import { generatePaypalID } from "@/lib/Paypal.js"

const props = defineProps({
  cartId: {
    type: String,
    required: true,
  },
})

const open = ref(false)
const processing = ref(false)
const buttons = [generatePaypalID(), "checkout-paypal-tab"]
let button = null
let interval = null

const attachInterval = () => {
  interval = setInterval(() => {
    const btns = document.querySelector(".paypal-buttons")
    btns && (btns.style.height = "50px")
  }, 400)
}

const runInitializer = async () => {
  await fetchWindowObject("paypal")
  await initButtons()
}

const initButtons = async () => {
  if (window.location.pathname !== "/checkout") return

  const fundingSource = paypal.FUNDING.PAYPAL

  button = paypal.Buttons({
    fundingSource,
    style: {
      height: 50,
      disableMaxWidth: true,
    },
    createOrder: async function (data, actions) {
      if (open.value) return

      open.value = true
      const response = await Http.get("/cart/paypal")
      const paypalData = response.data.paypal

      return actions.order.create(paypalData)
    },
    onApprove: async function (data, actions) {
      if (processing.value) return

      processing.value = true
      const promise = actions.order.capture()
      const details = await promise

      const response = await Http.post("/cart/paypal", {
        orderID: data.orderID,
        cartID: props.cartId,
      })

      if (response.status === 200) {
        Turbo.visit(`/carts/${response.data.cart_id}/success`, { action: "replace" })
        actions.close()
      }

      open.value = false
      processing.value = false
      return promise
    },
    onShippingChange: async function (data, actions) {
      const country = data.shipping_address.country_code

      const response = await Http.patch("/cart/paypal", {
        id: props.cartId,
        cart: { country },
      })

      if (response.status === 200) {
        return actions.order.patch([response.data])
      }

      return actions.reject()
    },
    onError: function (error) {
      console.error("PayPal button error:", error)
      open.value = false
    },
    onCancel: function () {
      open.value = false
    }
  })

  if (button.isEligible()) {
    await mountButton(`#${buttons[0]}`)
    await mountButton(`#${buttons[1]}`)

    setTimeout(reinitIfNotInitialized, 1000)
    attachInterval()
  }
}

const reinitIfNotInitialized = () => {
  if (isCreated(`#${buttons[0]}`) || isCreated(`#${buttons[1]}`)) {
    initButtons()
  }
}

const isCreated = (id) => {
  return document.querySelector(id).innerHTML.trim() === ""
}

const mountButton = async (id) => {
  try {
    await button.render(id)
  } catch {
    setTimeout(initButtons, 300)
    throw new Error("Error while PayPal buttons are initializing")
  }
}

onMounted(() => {
  nextTick(runInitializer)
})

onBeforeUnmount(() => {
  clearInterval(interval)
})
</script>
